import { useMemo, useState } from "react";
import { isAxiosError } from "axios";
import { Building2, Network, Search, Truck } from "lucide-react";
import { CalidadControlModificacionDeleteModal } from "../../../components/control-modificaciones/CalidadControlModificacionDeleteModal";
import { CalidadControlModificacionFormModal } from "../../../components/control-modificaciones/CalidadControlModificacionFormModal";
import { CalidadControlModificacionesTable } from "../../../components/control-modificaciones/CalidadControlModificacionesTable";
import { Iso900114001OverviewCard } from "../../../components/shared/Iso900114001OverviewCard";
import { Iso900114001SectionHeader } from "../../../components/shared/Iso900114001SectionHeader";
import { Iso900114001Tabs } from "../../../components/shared/Iso900114001Tabs";
import { useIso900114001DebouncedValue } from "../../../components/shared/useIso900114001FilteredRequest";
import {
  createCalidadSuministradoresControlModificacion,
  createCalidadSuministradorDirecto,
  createCalidadSuministradorGrupo,
  createCalidadSuministradorIndirecto,
  deleteCalidadSuministradoresControlModificacion,
  deleteCalidadSuministradorDirecto,
  deleteCalidadSuministradorGrupo,
  deleteCalidadSuministradorIndirecto,
  updateCalidadSuministradoresControlModificacion,
  updateCalidadSuministradorDirecto,
  updateCalidadSuministradorGrupo,
  updateCalidadSuministradorIndirecto,
} from "../api/suministradores-homologados";
import { useCalidadSuministradoresHomologados } from "../hooks/useCalidadSuministradoresHomologados";
import type {
  CalidadControlModificacion,
  CalidadControlModificacionPayload,
} from "../../../types/control-modificaciones";
import type {
  CalidadDirectLikePayload,
  CalidadIndirectoPayload,
  CalidadSuministradorHomologadoDirecto,
  CalidadSuministradorHomologadoGrupo,
  CalidadSuministradorHomologadoIndirecto,
  CalidadSuministradorKind,
} from "../types/suministradores-homologados";
import {
  createEmptyDirectLikeForm,
  createEmptyIndirectoForm,
  mapDirectLikeFormToPayload,
  mapDirectoToPayload,
  mapGrupoToPayload,
  mapIndirectoFormToPayload,
  mapIndirectoToPayload,
} from "../types/suministradores-homologados";
import { CalidadSuministradorFormModal } from "./CalidadSuministradorFormModal";
import { CalidadSuministradoresBlock } from "./CalidadSuministradoresBlock";
import { CalidadSuministradoresDeleteModal } from "./CalidadSuministradoresDeleteModal";

type Props = {
  onBack: () => void;
};

type FormState =
  | {
      open: false;
    }
  | {
      open: true;
      kind: CalidadSuministradorKind;
      mode: "create" | "edit";
      title: string;
      record:
        | CalidadSuministradorHomologadoIndirecto
        | CalidadSuministradorHomologadoDirecto
        | CalidadSuministradorHomologadoGrupo
        | null;
    };

type DeleteState =
  | {
      open: false;
    }
  | {
      open: true;
      kind: CalidadSuministradorKind | "control";
      title: string;
      id: number;
      nombre: string | null;
      description?: string;
    };

type ControlFormState =
  | { open: false }
  | {
      open: true;
      mode: "create" | "edit";
      row: CalidadControlModificacion | null;
    };

type SupplierTab = "directos" | "indirectos" | "grupo" | "control";

export function CalidadSuministradoresSection({ onBack }: Props) {
  const { data, controlModificaciones, loading, error, reload } =
    useCalidadSuministradoresHomologados(true);
  const [activeTab, setActiveTab] = useState<SupplierTab>("directos");
  const [filters, setFilters] = useState({
    directos: "",
    indirectos: "",
    grupo: "",
    control: "",
  });
  const [formState, setFormState] = useState<FormState>({ open: false });
  const [controlFormState, setControlFormState] = useState<ControlFormState>({
    open: false,
  });
  const [deleteState, setDeleteState] = useState<DeleteState>({ open: false });
  const [submitting, setSubmitting] = useState(false);
  const [formError, setFormError] = useState<string | null>(null);
  const [controlSubmitting, setControlSubmitting] = useState(false);
  const [controlFormError, setControlFormError] = useState<string | null>(null);
  const [deleting, setDeleting] = useState(false);
  const debouncedFilters = useIso900114001DebouncedValue(filters, 300);

  const filteredIndirectos = useMemo(
    () => filterSuppliers(data.indirectos, debouncedFilters.indirectos),
    [data.indirectos, debouncedFilters.indirectos],
  );
  const filteredDirectos = useMemo(
    () => filterSuppliers(data.directos, debouncedFilters.directos),
    [data.directos, debouncedFilters.directos],
  );
  const filteredGrupo = useMemo(
    () => filterSuppliers(data.grupo, debouncedFilters.grupo),
    [data.grupo, debouncedFilters.grupo],
  );
  const filteredControl = useMemo(
    () => controlModificaciones.filter((row) => {
      const term = debouncedFilters.control.trim().toLowerCase();
      if (!term) return true;
      return `${row.fecha} ${row.modificaciones_realizadas}`.toLowerCase().includes(term);
    }),
    [controlModificaciones, debouncedFilters.control],
  );

  const activeSearch = filters[activeTab];
  const activeCount =
    activeTab === "directos"
      ? filteredDirectos.length
      : activeTab === "indirectos"
        ? filteredIndirectos.length
        : activeTab === "grupo"
          ? filteredGrupo.length
          : filteredControl.length;

  const formInitialValues = useMemo(() => {
    if (!formState.open) {
      return createEmptyIndirectoForm();
    }

    if (formState.kind === "indirecto") {
      return formState.record
        ? mapIndirectoToPayload(formState.record as CalidadSuministradorHomologadoIndirecto)
        : createEmptyIndirectoForm();
    }

    if (formState.kind === "directo") {
      return formState.record
        ? mapDirectoToPayload(formState.record as CalidadSuministradorHomologadoDirecto)
        : createEmptyDirectLikeForm();
    }

    return formState.record
      ? mapGrupoToPayload(formState.record as CalidadSuministradorHomologadoGrupo)
      : createEmptyDirectLikeForm();
  }, [formState]);

  const openCreate = (kind: CalidadSuministradorKind) => {
    setFormError(null);
    setFormState({
      open: true,
      kind,
      mode: "create",
      title: getKindTitle(kind),
      record: null,
    });
  };

  const openEdit = (
    kind: CalidadSuministradorKind,
    record:
      | CalidadSuministradorHomologadoIndirecto
      | CalidadSuministradorHomologadoDirecto
      | CalidadSuministradorHomologadoGrupo,
  ) => {
    setFormError(null);
    setFormState({
      open: true,
      kind,
      mode: "edit",
      title: getKindTitle(kind),
      record,
    });
  };

  const handleSubmit = async (
    values: CalidadIndirectoPayload | CalidadDirectLikePayload,
  ) => {
    if (!formState.open) {
      return;
    }

    setSubmitting(true);
    setFormError(null);

    try {
      if (formState.kind === "indirecto") {
        const payload = mapIndirectoFormToPayload(values as CalidadIndirectoPayload);

        if (formState.mode === "create") {
          await createCalidadSuministradorIndirecto(payload);
        } else {
          await updateCalidadSuministradorIndirecto(formState.record!.id, payload);
        }
      } else if (formState.kind === "directo") {
        const payload = mapDirectLikeFormToPayload(values as CalidadDirectLikePayload);

        if (formState.mode === "create") {
          await createCalidadSuministradorDirecto(payload);
        } else {
          await updateCalidadSuministradorDirecto(formState.record!.id, payload);
        }
      } else {
        const payload = mapDirectLikeFormToPayload(values as CalidadDirectLikePayload);

        if (formState.mode === "create") {
          await createCalidadSuministradorGrupo(payload);
        } else {
          await updateCalidadSuministradorGrupo(formState.record!.id, payload);
        }
      }

      setFormState({ open: false });
      await reload();
    } catch (requestError) {
      setFormError(
        getRequestErrorMessage(
          requestError,
          "No se pudo guardar el suministrador homologado.",
        ),
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmitControl = async (
    payload: CalidadControlModificacionPayload,
  ) => {
    if (!controlFormState.open) {
      return;
    }

    setControlSubmitting(true);
    setControlFormError(null);

    try {
      if (controlFormState.mode === "create") {
        await createCalidadSuministradoresControlModificacion(payload);
      } else if (controlFormState.row) {
        await updateCalidadSuministradoresControlModificacion(
          controlFormState.row.id,
          payload,
        );
      }

      setControlFormState({ open: false });
      await reload();
    } catch (requestError) {
      setControlFormError(
        getRequestErrorMessage(
          requestError,
          "No se pudo guardar la modificación del documento.",
        ),
      );
    } finally {
      setControlSubmitting(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteState.open) {
      return;
    }

    setDeleting(true);

    try {
      if (deleteState.kind === "control") {
        await deleteCalidadSuministradoresControlModificacion(deleteState.id);
      } else if (deleteState.kind === "indirecto") {
        await deleteCalidadSuministradorIndirecto(deleteState.id);
      } else if (deleteState.kind === "directo") {
        await deleteCalidadSuministradorDirecto(deleteState.id);
      } else {
        await deleteCalidadSuministradorGrupo(deleteState.id);
      }

      setDeleteState({ open: false });
      await reload();
    } finally {
      setDeleting(false);
    }
  };

  return (
    <section className="calidad-section">
      <Iso900114001SectionHeader
        eyebrow="Catálogo homologado"
        title="Suministradores Homologados"
        description="Gestión de proveedores y suministradores homologados clasificados como indirectos, directos y grupo."
        onBack={onBack}
        onRefresh={() => {
          void reload();
        }}
        refreshing={loading}
      />

      <div className="iso9001-overview-grid">
        <Iso900114001OverviewCard
          icon={Truck}
          label="Indirectos"
          value={loading ? "..." : data.indirectos.length}
        />
        <Iso900114001OverviewCard
          icon={Building2}
          label="Directos"
          value={loading ? "..." : data.directos.length}
        />
        <Iso900114001OverviewCard
          icon={Network}
          label="Grupo"
          value={loading ? "..." : data.grupo.length}
        />
      </div>

      <Iso900114001Tabs
        tabs={[
          { id: "directos", label: "Directos", count: data.directos.length },
          { id: "indirectos", label: "Indirectos", count: data.indirectos.length },
          { id: "grupo", label: "Grupo", count: data.grupo.length },
          {
            id: "control",
            label: "Control documental",
            count: controlModificaciones.length,
          },
        ]}
        activeTab={activeTab}
        onChange={setActiveTab}
        ariaLabel="Tablas de suministradores homologados"
      />

      <div className="calidad-suppliers-tab-filters">
        <label className="calidad-suppliers-tab-search">
          <span>{getFilterLabel(activeTab)}</span>
          <div>
            <Search size={15} />
            <input
              value={activeSearch}
              onChange={(event) => setFilters((current) => ({ ...current, [activeTab]: event.target.value }))}
              placeholder={getFilterPlaceholder(activeTab)}
            />
          </div>
        </label>

        <div className="calidad-suppliers-filter-meta">
          {loading ? "Cargando..." : `${activeCount} registros visibles`}
        </div>
      </div>

      {activeTab === "directos" ? (
        <CalidadSuministradoresBlock
          kind="directo"
          title="Proveedores directos"
          rows={filteredDirectos}
          loading={loading}
          error={error}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => openCreate("directo")}
          onEdit={(row) => openEdit("directo", row)}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "directo",
              title: "Proveedores directos",
              id: row.id,
              nombre: row.nombre,
            })
          }
        />
      ) : activeTab === "indirectos" ? (
        <CalidadSuministradoresBlock
          kind="indirecto"
          title="Proveedores indirectos"
          rows={filteredIndirectos}
          loading={loading}
          error={error}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => openCreate("indirecto")}
          onEdit={(row) => openEdit("indirecto", row)}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "indirecto",
              title: "Proveedores indirectos",
              id: row.id,
              nombre: row.nombre,
            })
          }
        />
      ) : activeTab === "grupo" ? (
        <CalidadSuministradoresBlock
          kind="grupo"
          title="Proveedores del grupo"
          rows={filteredGrupo}
          loading={loading}
          error={error}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => openCreate("grupo")}
          onEdit={(row) => openEdit("grupo", row)}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "grupo",
              title: "Proveedores del grupo",
              id: row.id,
              nombre: row.nombre,
            })
          }
        />
      ) : (
        <CalidadControlModificacionesTable
          rows={filteredControl}
          loading={loading}
          error={error}
          onRetry={() => {
            void reload();
          }}
          onCreate={() => {
            setControlFormError(null);
            setControlFormState({ open: true, mode: "create", row: null });
          }}
          onEdit={(row) => {
            setControlFormError(null);
            setControlFormState({ open: true, mode: "edit", row });
          }}
          onDelete={(row) =>
            setDeleteState({
              open: true,
              kind: "control",
              title: "Eliminar modificación",
              id: row.id,
              nombre: null,
              description: `Se eliminará la modificación registrada en ${row.fecha}.`,
            })
          }
        />
      )}

      <CalidadSuministradorFormModal
        open={formState.open}
        kind={formState.open ? formState.kind : "indirecto"}
        mode={formState.open ? formState.mode : "create"}
        title={formState.open ? formState.title : "Suministradores homologados"}
        initialValues={formInitialValues}
        submitting={submitting}
        error={formError}
        onClose={() => setFormState({ open: false })}
        onSubmit={handleSubmit}
      />

      <CalidadControlModificacionFormModal
        open={controlFormState.open}
        mode={controlFormState.open ? controlFormState.mode : "create"}
        initialValues={controlFormState.open ? controlFormState.row : null}
        submitting={controlSubmitting}
        error={controlFormError}
        onClose={() => setControlFormState({ open: false })}
        onSubmit={handleSubmitControl}
      />

      <CalidadSuministradoresDeleteModal
        open={deleteState.open && deleteState.kind !== "control"}
        title={deleteState.open && deleteState.kind !== "control" ? deleteState.title : ""}
        nombre={
          deleteState.open && deleteState.kind !== "control" ? deleteState.nombre : null
        }
        deleting={deleting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={handleDelete}
      />

      <CalidadControlModificacionDeleteModal
        open={deleteState.open && deleteState.kind === "control"}
        title={deleteState.open && deleteState.kind === "control" ? deleteState.title : ""}
        description={
          deleteState.open && deleteState.kind === "control"
            ? deleteState.description ?? ""
            : ""
        }
        deleting={deleting}
        onClose={() => setDeleteState({ open: false })}
        onConfirm={handleDelete}
      />
    </section>
  );
}


function filterSuppliers<TRow extends object>(rows: TRow[], search: string): TRow[] {
  const term = search.trim().toLowerCase();

  if (!term) {
    return rows;
  }

  return rows.filter((row) =>
    Object.values(row).some((value) =>
      value !== null && value !== undefined && String(value).toLowerCase().includes(term),
    ),
  );
}

function getFilterLabel(tab: SupplierTab) {
  if (tab === "control") return "Buscar en control documental";
  if (tab === "indirectos") return "Buscar en indirectos";
  if (tab === "grupo") return "Buscar en grupo";
  return "Buscar en directos";
}

function getFilterPlaceholder(tab: SupplierTab) {
  if (tab === "control") return "Fecha o modificación...";
  if (tab === "indirectos") return "Nombre, material, tipología, observaciones...";
  if (tab === "grupo") return "Nombre, servicio, comentarios...";
  return "Nombre, servicio, comentarios...";
}


function getKindTitle(kind: CalidadSuministradorKind) {
  switch (kind) {
    case "indirecto":
      return "Proveedor indirecto";
    case "directo":
      return "Proveedor directo";
    case "grupo":
      return "Proveedor del grupo";
  }
}

function getRequestErrorMessage(error: unknown, fallback: string) {
  if (!isAxiosError(error)) {
    return fallback;
  }

  const fieldErrors = error.response?.data?.errors;
  const firstError =
    fieldErrors && typeof fieldErrors === "object"
      ? Object.values(fieldErrors).find(Array.isArray)?.[0]
      : null;

  if (typeof firstError === "string") {
    return firstError;
  }

  const message = error.response?.data?.message;

  return typeof message === "string" ? message : fallback;
}
